import type { MfaStep } from './signIn.types';
import { PageTemplate } from '@components/template';
import { LoginStep } from './LoginStep';
import { MfaSetup } from './MfaSetup';
import { MfaVerification } from './MfaVerification';
import { OAuthProcessingView } from './OAuthProcessingView';
import { SsoProviderSelectStep } from './SsoProviderSelectStep';
import { useSignInFlow } from './useSignInFlow';

type Props = {
  isItAdminPage?: boolean;
};

/** MFA 단계 - 하위 호환을 위해 re-export */
export type { MfaStep };

export const SignIn = ({ isItAdminPage }: Props) => {
  const {
    step,
    email,
    ssoProviders,
    isAuthenticationFailed,
    loginErrorMessage,
    invalidTenant,
    isLoginPending,
    isLookupPending,
    isRedirecting,
    mfaQrCodeUrl,
    mfaSecret,
    mfaError,
    mfaLoading,
    showGoogleLogin,
    showMicrosoftLogin,
    disablePasswordLogin,
    showSsoAsDefault,
    setEmail,
    handleEmailContinue,
    handlePasswordLogin,
    handleSsoProviderSelect,
    handleMfaVerify,
    handleChangeMfaStep,
    handleBackToEmail,
    rememberEmail,
    setRememberEmail,
    handleGoogleLogin,
    handleMicrosoftLogin,
  } = useSignInFlow({ isItAdminPage });

  // OAuth/SSO 콜백 처리 중 로딩
  if (step === 'OAUTH_PROCESSING' || step === 'SSO_REDIRECTING') {
    return <OAuthProcessingView />;
  }
  // MFA 설정 화면
  if (step === 'MFA_SETUP' && mfaQrCodeUrl && mfaSecret) {
    return (
      <MfaSetup
        qrCodeUrl={mfaQrCodeUrl}
        secret={mfaSecret}
        onVerify={handleMfaVerify}
        isLoading={mfaLoading}
        error={mfaError}
      />
    );
  }

  // MFA 인증 화면
  if (step === 'MFA_VERIFY') {
    return (
      <MfaVerification
        onVerify={handleMfaVerify}
        onBack={handleBackToEmail}
        isLoading={mfaLoading}
        error={mfaError}
        onChangeMfaStep={handleChangeMfaStep}
      />
    );
  }

  // SSO 프로바이더 선택 화면
  if (step === 'SSO_PROVIDER_SELECT') {
    return (
      <PageTemplate>
        <SsoProviderSelectStep
          email={email}
          providers={ssoProviders}
          onBack={handleBackToEmail}
          onSelectProvider={handleSsoProviderSelect}
          isRedirecting={isRedirecting}
          isItAdminPage={isItAdminPage}
        />
      </PageTemplate>
    );
  }

  // 기본: 이메일 입력 화면
  return (
    <PageTemplate>
      <LoginStep
        email={email}
        onEmailChange={setEmail}
        onContinue={handleEmailContinue}
        onBack={handleBackToEmail}
        onSubmit={handlePasswordLogin}
        isPasswordStep={step === 'PASSWORD_INPUT'}
        isLookupPending={isLookupPending}
        isLoginPending={isLoginPending}
        isAuthenticationFailed={isAuthenticationFailed}
        loginErrorMessage={loginErrorMessage}
        invalidTenant={invalidTenant}
        showGoogleLogin={showGoogleLogin}
        showMicrosoftLogin={showMicrosoftLogin}
        disablePasswordLogin={disablePasswordLogin}
        showSsoAsDefault={showSsoAsDefault}
        onGoogleLogin={handleGoogleLogin}
        onMicrosoftLogin={handleMicrosoftLogin}
        rememberEmail={rememberEmail}
        onRememberEmailChange={setRememberEmail}
        isItAdminPage={isItAdminPage}
      />
    </PageTemplate>
  );
};
